<template lang="pug">
  .container
    h1.title.is-2 Sync pokedex
    .content
      p
        | Finds pokemons and generations missing in the pokedex, using species data from
        |  #[a(href="https://github.com/PokeAPI/pokeapi" target="_blank") PokeAPI].
        |  Nothing is written until you review the changes and click #[b Apply].
      p
        | New pokemons are not marked as collectable unless availability is used, so they stay hidden
        |  in the collection until you mark them as released.
    .columns.is-vcentered.is-multiline
      .column.is-narrow
        b-checkbox(v-model="useAvailability" :disabled="isChecking || isApplying")
          | Prefill collectable, shiny and purified from #[a(href="https://pogoapi.net" target="_blank") pogoapi.net]
      .column.is-narrow
        button.button.is-dark(@click="check()" :class="{ 'is-loading': isChecking }" :disabled="isApplying")
          span.icon.is-small
            font-awesome-icon(:icon="['fas', 'search']")
          span Check for changes
      .column.is-narrow
        router-link(to="/admin") Back to admin
    .notification.is-danger(v-if="error") {{ error }}
    .notification.is-warning(v-if="availabilityError")
      | Could not fetch availability from pogoapi.net ({{ availabilityError }}). Showing changes without availability.
    template(v-if="plan")
      .notification(v-if="!hasChanges")
        | The pokedex is up to date. Nothing to change.
      template(v-else)
        .columns.is-mobile.is-vcentered.summary
          .column
            b {{ selectedPokemons.length }} of {{ pokemonRows.length }} new pokemons
            template(v-if="selectedGenerations.length")
              | , {{ selectedGenerations.length }} new generations
            template(v-if="flagRows.length")
              | , {{ selectedFlagChanges.length }} of {{ flagRows.length }} flag changes
            |  selected
          .column.is-narrow
            button.button.is-primary(
              @click="confirmApply()"
              :class="{ 'is-loading': isApplying }"
              :disabled="changeCount === 0"
            ) Apply {{ changeCount }} changes

        template(v-if="generationIds.length")
          h2.title.is-4 New generations
          p.help Added together with the first selected pokemon from that generation.
          table.table.is-narrow
            thead
              tr
                th Id
                th Number
                th Name
                th Color
                th
            tbody
              tr(v-for="id in generationIds" :key="id" :class="{ 'has-text-grey-light': !selectedGenerations.includes(id) }")
                td {{ id }}
                td {{ plan.newGenerations[id].number }}
                td {{ plan.newGenerations[id].name }}
                td
                  span.color-swatch(:style="{ backgroundColor: plan.newGenerations[id].color }")
                td {{ selectedGenerations.includes(id) ? 'Will be added' : 'Not needed' }}

        template(v-if="pokemonRows.length")
          h2.title.is-4 New pokemons
          .columns.is-vcentered.is-mobile.is-multiline
            .column.is-narrow
              b-select(v-model="generationFilter" placeholder="All generations")
                option(:value="null") All generations
                option(v-for="id in pokemonGenerationIds" :value="id" :key="id") {{ generationLabel(id) }}
            .column.is-narrow
              button.button.is-small(@click="selectPokemons(true)") Select all shown
              button.button.is-small(@click="selectPokemons(false)") Deselect all shown
          table.table.is-striped.is-narrow.is-fullwidth.is-hoverable
            thead
              tr
                th
                th No
                th Pokemon
                th.is-hidden-mobile Generation
                th.is-hidden-mobile Types
                th.is-hidden-mobile Family
                th.is-hidden-mobile Legendary
                th Availability
            tbody
              tr(v-for="row in shownPokemonRows" :key="row.pokemon.number" :class="{ 'is-deselected': !row.selected }")
                td
                  b-checkbox(v-model="row.selected")
                td {{ row.pokemon.number }}
                td
                  img.poke-image(:src="`https://db.pokemongohub.net/images/official/detail/${row.pokemon.number}.png`")
                  b {{ row.pokemon.name }}
                td.is-hidden-mobile {{ generationLabel(row.pokemon.generation) }}
                td.is-hidden-mobile {{ [row.pokemon.type1, row.pokemon.type2].filter(Boolean).join(' / ') }}
                td.is-hidden-mobile {{ row.pokemon.family }}
                td.is-hidden-mobile {{ row.pokemon.legendary }}
                td
                  .collection-buttons
                    b-tooltip(label="Collectable (released)" type="is-dark")
                      button.button.collection-button.is-small(
                        :class="{ 'is-dark': row.pokemon.collectable }"
                        @click="row.pokemon.collectable = !row.pokemon.collectable"
                      )
                        font-awesome-icon(:icon="['fas', 'check']", v-if="row.pokemon.collectable")
                        font-awesome-icon(:icon="['far', 'square']", v-else)
                    b-tooltip(label="Shiny available" type="is-dark")
                      button.button.collection-button.is-small(
                        :class="{ 'is-shiny': row.pokemon.shiny }"
                        @click="row.pokemon.shiny = !row.pokemon.shiny"
                      )
                        img.collection-image(src="@/assets/shiny-stars.png")
                    b-tooltip(label="Shadow/purified available" type="is-dark")
                      button.button.collection-button.is-small(
                        :class="{ 'is-purified': row.pokemon.purified }"
                        @click="row.pokemon.purified = !row.pokemon.purified"
                      )
                        img.collection-image(src="@/assets/purified.png")

        template(v-if="flagRows.length")
          h2.title.is-4 Flags to turn on for existing pokemons
          p.help From pogoapi.net. Flags are only turned on, never off.
          table.table.is-striped.is-narrow.is-hoverable
            thead
              tr
                th
                th No
                th Pokemon
                th Change
            tbody
              tr(v-for="row in flagRows" :key="`${row.change.key}-${row.change.flag}`" :class="{ 'is-deselected': !row.selected }")
                td
                  b-checkbox(v-model="row.selected")
                td {{ row.change.number }}
                td {{ row.change.name }}
                td {{ row.change.flag }}: off → on
</template>

<script>
import { db } from '@/db';
import { fetchSpecies, fetchAvailability, planSync, buildUpdates } from '@/utils/pokedex-sync.js';

export default {
  name: 'SyncPokedex',
  data() {
    return {
      useAvailability: false,
      isChecking: false,
      isApplying: false,
      error: null,
      availabilityError: null,
      plan: null,
      generations: {},
      pokemonRows: [],
      flagRows: [],
      generationFilter: null,
    };
  },
  computed: {
    hasChanges() {
      return this.pokemonRows.length > 0 || this.flagRows.length > 0;
    },
    generationIds() {
      return Object.keys(this.plan.newGenerations).filter(id => this.pokemonGenerationIds.includes(id));
    },
    pokemonGenerationIds() {
      return [...new Set(this.pokemonRows.map(row => row.pokemon.generation))];
    },
    shownPokemonRows() {
      if (!this.generationFilter) return this.pokemonRows;
      return this.pokemonRows.filter(row => row.pokemon.generation === this.generationFilter);
    },
    selectedPokemons() {
      return this.pokemonRows.filter(row => row.selected).map(row => row.pokemon);
    },
    selectedGenerations() {
      return [...new Set(this.selectedPokemons.map(pokemon => pokemon.generation))].filter(
        id => this.plan.newGenerations[id]
      );
    },
    selectedFlagChanges() {
      return this.flagRows.filter(row => row.selected).map(row => row.change);
    },
    updates() {
      return buildUpdates({
        newGenerations: this.plan.newGenerations,
        newPokemons: this.selectedPokemons,
        flagChanges: this.selectedFlagChanges,
      });
    },
    changeCount() {
      return this.plan ? Object.keys(this.updates).length : 0;
    },
  },
  methods: {
    async check() {
      this.isChecking = true;
      this.error = null;
      this.availabilityError = null;
      this.plan = null;
      this.generationFilter = null;
      try {
        const [species, availability, pokedex, generations] = await Promise.all([
          fetchSpecies(),
          this.useAvailability
            ? fetchAvailability().catch(error => {
                this.availabilityError = error.message;
                return null;
              })
            : null,
          db.pokedex(),
          db.generations(),
        ]);
        const plan = planSync({ species, availability, pokedex, generations });
        this.generations = generations || {};
        this.pokemonRows = plan.newPokemons.map(pokemon => ({ pokemon, selected: true }));
        this.flagRows = plan.flagChanges.map(change => ({ change, selected: true }));
        this.plan = plan;
      } catch (error) {
        this.error = `Could not check for changes: ${error.message}`;
      }
      this.isChecking = false;
    },
    generationLabel(id) {
      const generation = this.generations[id] || (this.plan && this.plan.newGenerations[id]);
      return generation ? `${generation.number} ${generation.name}` : id;
    },
    selectPokemons(selected) {
      this.shownPokemonRows.forEach(row => (row.selected = selected));
    },
    confirmApply() {
      const parts = [`<b>${this.selectedPokemons.length}</b> new pokemons`];
      if (this.selectedGenerations.length) {
        parts.push(
          `<b>${this.selectedGenerations.length}</b> new generations (${this.selectedGenerations.join(', ')})`
        );
      }
      if (this.selectedFlagChanges.length) {
        parts.push(`<b>${this.selectedFlagChanges.length}</b> flag changes on existing pokemons`);
      }
      this.$dialog.confirm({
        title: 'Apply changes',
        message: `This will write ${parts.join(', ')} to the pokedex. Existing pokemons are not overwritten.`,
        type: 'is-dark',
        confirmText: 'Apply',
        onConfirm: () => this.apply(),
      });
    },
    async apply() {
      this.isApplying = true;
      const count = this.changeCount;
      try {
        // One multi-path update, so either everything is written or nothing is
        await db.database.ref().update(this.updates);
        this.$toast.open({ message: `Applied ${count} changes.`, type: 'is-black', queue: false });
        this.isApplying = false;
        await this.check();
      } catch (error) {
        this.error = `Failed to apply changes: ${error.message}. Nothing was written.`;
        this.isApplying = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.summary {
  position: sticky;
  top: 0;
  z-index: 2;
  background: white;
  border-bottom: 1px solid #dbdbdb;
  margin-bottom: 1rem;
}

.table td {
  vertical-align: middle;
}

.is-deselected {
  opacity: 0.45;
}

.poke-image {
  vertical-align: middle;
  height: 42px;
  padding-right: 4px;
}

.color-swatch {
  display: inline-block;
  width: 40px;
  height: 16px;
  border-radius: 4px;
}

.collection-buttons .button {
  margin-right: 4px;
}

.help {
  margin-top: -1rem;
  margin-bottom: 0.75rem;
}
</style>
